import React, { useEffect, useState } from 'react';
import {
  User,
  Mail,
  MapPin,
  Shield,
  Stethoscope,
  IdCard,
  AlertTriangle,
  Edit,
} from 'lucide-react';
import { Patient } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDOB, calculateAge } from '@/lib/dateUtils';
import { toTitleCase } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';
import { allergiesService } from '@/services/allergiesService';
import { parseMedicalHistory } from '@/components/ui/medical-history-multi-select';

interface PatientProfileTabProps {
  patient: Patient | null;
  onEdit?: () => void;
}

const EMPTY = '—';

const fmt = (v?: string | number | null): string => {
  if (v == null) return EMPTY;
  const s = String(v).trim();
  return s === '' ? EMPTY : s;
};

const formatPreferred = (
  prefs?: Patient['preferred_contact_methods'],
): string => {
  if (!prefs) return EMPTY;
  const labels: string[] = [];
  if (prefs.email) labels.push('Email');
  if (prefs.sms) labels.push('SMS');
  if (prefs.phone) labels.push('Phone');
  return labels.length ? labels.join(', ') : EMPTY;
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

const Field: React.FC<FieldProps> = ({ label, value, className }) => (
  <div className={className}>
    <div className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>
      {label}
    </div>
    <div className='mt-0.5 text-sm text-slate-900 break-words'>{value || EMPTY}</div>
  </div>
);

interface SectionProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, icon: Icon, children }) => (
  <section className='rounded-lg border border-slate-200 bg-white'>
    <header className='flex items-center gap-2 border-b border-slate-100 px-5 py-3'>
      <Icon className='h-4 w-4 text-slate-500' />
      <h3 className='text-sm font-semibold text-slate-900'>{title}</h3>
    </header>
    <div className='px-5 py-4'>{children}</div>
  </section>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
    {children}
  </div>
);

const contactsByKey = (patient: Patient | null, key: 'email' | 'phone') =>
  (patient?.contacts ?? []).filter((c) => c.key === key);

const billingAddress = (patient: Patient | null) =>
  patient?.addresses?.find((a) => a.address_type !== 'shipping') ??
  patient?.addresses?.[0];

const shippingAddress = (patient: Patient | null) =>
  patient?.addresses?.find((a) => a.address_type === 'shipping');

const renderAddress = (a?: ReturnType<typeof billingAddress>): React.ReactNode => {
  if (!a) return EMPTY;
  const line1 = a.line_1?.trim();
  const line2 = a.line_2?.trim();
  const cityState = [a.city, a.stateInfo?.code || a.state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, a.postal_code].filter(Boolean).join(' ');
  const lines = [line1, line2, cityStateZip, a.country].filter(Boolean) as string[];
  if (lines.length === 0) return EMPTY;
  return (
    <div className='space-y-0.5'>
      {lines.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
};

/**
 * Read-only profile view that mirrors the create-patient form. Renders
 * every field captured during patient creation grouped into the same
 * sections (basic, contact, addresses, assignment, insurance, clinical,
 * medical history) so practitioners can verify what was recorded at a
 * glance without re-opening the edit drawer.
 */
export default function PatientProfileTab({
  patient,
  onEdit,
}: PatientProfileTabProps) {
  const { clinic } = useClinic();
  const [allergies, setAllergies] = useState<string[]>([]);
  const clinicId = clinic?.id;
  const patientId = patient?.id;
  useEffect(() => {
    if (!clinicId || !patientId) return;
    let cancelled = false;
    allergiesService
      .getActiveMedicationAllergyNames(clinicId, patientId)
      .then((names) => {
        if (!cancelled) setAllergies(names);
      })
      .catch(() => {
        if (!cancelled) setAllergies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, patientId]);

  if (!patient) {
    return (
      <div className='rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500'>
        Loading patient profile…
      </div>
    );
  }

  const emails = contactsByKey(patient, 'email');
  const phones = contactsByKey(patient, 'phone');
  const defaultEmail = emails.find((c) => c.is_default)?.value || patient.email;
  const defaultPhone = phones.find((c) => c.is_default)?.value || patient.phone;
  const altEmails = emails.filter((c) => c.value !== defaultEmail);
  const altPhones = phones.filter((c) => c.value !== defaultPhone);

  const billing = billingAddress(patient);
  const shipping = shippingAddress(patient);

  const age =
    patient.dob && Number.isFinite(calculateAge(patient.dob))
      ? `${calculateAge(patient.dob)} yrs`
      : null;

  const history = parseMedicalHistory(patient.medical_history);

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-lg font-semibold text-slate-900'>Patient profile</h2>
          <p className='text-sm text-slate-500'>
            All fields captured during patient creation.
          </p>
        </div>
        {onEdit && (
          <Button variant='outline' size='sm' onClick={onEdit}>
            <Edit className='mr-1.5 h-3.5 w-3.5' />
            Edit
          </Button>
        )}
      </div>

      <Section title='Basic information' icon={User}>
        <Grid>
          <Field label='First name' value={fmt(patient.first_name)} />
          <Field label='Middle name' value={fmt(patient.middle_name)} />
          <Field label='Last name' value={fmt(patient.last_name)} />
          <Field
            label='Date of birth'
            value={
              patient.dob ? (
                <span>
                  {formatDOB(patient.dob)}
                  {age && <span className='ml-1 text-slate-500'>({age})</span>}
                </span>
              ) : (
                EMPTY
              )
            }
          />
          <Field label='Gender' value={patient.gender ? toTitleCase(patient.gender) : EMPTY} />
          <Field
            label='Status'
            value={
              <Badge
                variant='outline'
                className={
                  patient.status === 'active'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 bg-slate-50 text-slate-600'
                }
              >
                {toTitleCase(patient.status)}
              </Badge>
            }
          />
          {patient.mrn && <Field label='MRN' value={patient.mrn} />}
          {patient.ssn && <Field label='SSN' value={patient.ssn} />}
          {patient.species && patient.species !== 'human' && (
            <Field label='Species' value={toTitleCase(patient.species)} />
          )}
          {patient.animal_owner_name && (
            <Field label='Owner' value={patient.animal_owner_name} />
          )}
        </Grid>
      </Section>

      <Section title='Contact information' icon={Mail}>
        <Grid>
          <Field
            label='Primary email'
            value={
              defaultEmail ? (
                <a className='text-brand-teal hover:underline' href={`mailto:${defaultEmail}`}>
                  {defaultEmail}
                </a>
              ) : (
                EMPTY
              )
            }
          />
          <Field
            label='Primary phone'
            value={
              defaultPhone ? (
                <a className='text-brand-teal hover:underline' href={`tel:${defaultPhone}`}>
                  {defaultPhone}
                </a>
              ) : (
                EMPTY
              )
            }
          />
          <Field
            label='Emergency contact'
            value={
              patient.emergency_contact_phone ? (
                <a
                  className='text-brand-teal hover:underline'
                  href={`tel:${patient.emergency_contact_phone}`}
                >
                  {patient.emergency_contact_phone}
                </a>
              ) : (
                EMPTY
              )
            }
          />
          <Field label='Preferred contact' value={formatPreferred(patient.preferred_contact_methods)} />
          {altEmails.length > 0 && (
            <Field
              label='Other emails'
              value={
                <div className='space-y-0.5'>
                  {altEmails.map((c, i) => (
                    <div key={i}>{c.value}</div>
                  ))}
                </div>
              }
            />
          )}
          {altPhones.length > 0 && (
            <Field
              label='Other phones'
              value={
                <div className='space-y-0.5'>
                  {altPhones.map((c, i) => (
                    <div key={i}>{c.value}</div>
                  ))}
                </div>
              }
            />
          )}
        </Grid>
      </Section>

      <Section title='Addresses' icon={MapPin}>
        <Grid>
          <Field label='Billing address' value={renderAddress(billing)} />
          <Field
            label='Shipping address'
            value={
              shipping ? renderAddress(shipping) : (
                <span className='text-slate-500'>Same as billing</span>
              )
            }
          />
        </Grid>
      </Section>

      <Section title='Care team' icon={Stethoscope}>
        <Grid>
          <Field
            label='Assigned practitioner'
            value={
              patient.practitioner?.full_name
                ? toTitleCase(patient.practitioner.full_name)
                : EMPTY
            }
          />
          <Field
            label='Clinic'
            value={fmt(patient.clinic?.name || clinic?.name)}
          />
        </Grid>
      </Section>

      <Section title='Insurance' icon={Shield}>
        <Grid>
          <Field label='Carrier' value={fmt(patient.insurance_carrier)} />
          <Field label='Member ID' value={fmt(patient.insurance_member_id)} />
          <Field label='Group ID' value={fmt(patient.insurance_group_id)} />
        </Grid>
      </Section>

      <Section title='Medical history' icon={IdCard}>
        <div className='space-y-4'>
          <div>
            <div className='flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500'>
              <AlertTriangle className='h-3 w-3' />
              Medication allergies
            </div>
            <div className='mt-1.5'>
              {allergies.length === 0 ? (
                <Badge variant='outline' className='border-slate-200 bg-slate-50 text-slate-600'>
                  No known allergies
                </Badge>
              ) : (
                <div className='flex flex-wrap gap-1.5'>
                  {allergies.map((a, i) => (
                    <Badge
                      key={i}
                      variant='outline'
                      className='border-rose-200 bg-rose-50 text-rose-700'
                    >
                      {a}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>
              History
            </div>
            {history.conditions.length === 0 && !history.other.trim() ? (
              <p className='mt-1 text-sm text-slate-700'>{EMPTY}</p>
            ) : (
              <div className='mt-1.5 space-y-2'>
                {history.conditions.length > 0 && (
                  <div className='flex flex-wrap gap-1.5'>
                    {history.conditions.map((c, i) => (
                      <Badge
                        key={i}
                        variant='outline'
                        className='border-slate-200 bg-slate-50 text-slate-700'
                      >
                        {c}
                      </Badge>
                    ))}
                  </div>
                )}
                {history.other.trim() && (
                  <p className='whitespace-pre-wrap text-sm text-slate-700'>
                    {history.other}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
